import React from "react";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Pencil,
  ShieldCheck,
  Trash2,
  UserCog,
} from "lucide-react";
import type { SspSystemUserResponse } from "../../../../api/frameworksApi";
import {
  FormField,
  FormInput,
  FormTextarea,
} from "../../../components/Modal";
import { Tip } from "../../../components/ui/tooltip";
import { toddmmyyyy } from "../constants";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { getPageWindow } from "../utils/pagination";

export interface SystemUsersTabProps {
  systemUsersLoading: boolean;
  systemUsers: SspSystemUserResponse[];
  pagSystemUsers: SspSystemUserResponse[];
  filtSystemUsers: SspSystemUserResponse[];
  editingSystemUserId: string | null;
  setEditingSystemUserId: React.Dispatch<React.SetStateAction<string | null>>;
  editSystemUserForm: {
    title: string;
    shortName: string;
    description: string;
    roleIds: string;
    privilegeTitle: string;
    functionsPerformed: string;
  };
  setEditSystemUserForm: React.Dispatch<
    React.SetStateAction<{
      title: string;
      shortName: string;
      description: string;
      roleIds: string;
      privilegeTitle: string;
      functionsPerformed: string;
    }>
  >;
  savingSystemUser: boolean;
  handleUpdateSystemUser: () => void;
  isPublishedSsp: boolean;
  handleDeleteSystemUser: (userId: string) => Promise<void> | void;
  deletingSystemUserId: string | null;
  tabPage: number;
  tabPageSize: number;
  setCurrentTabPagination: (pagination: {
    page: number;
    pageSize: number;
  }) => void;
}

export function SystemUsersTab({
  systemUsersLoading,
  systemUsers,
  pagSystemUsers,
  filtSystemUsers,
  editingSystemUserId,
  setEditingSystemUserId,
  editSystemUserForm,
  setEditSystemUserForm,
  savingSystemUser,
  handleUpdateSystemUser,
  isPublishedSsp,
  handleDeleteSystemUser,
  deletingSystemUserId,
  tabPage,
  tabPageSize,
  setCurrentTabPagination,
}: SystemUsersTabProps) {
  return (
    <>
      {systemUsersLoading ? (
        <LoadingSpinner text="Loading system users" />
      ) : systemUsers.length > 0 ? (
        <>
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            {/* Header */}
            <div className="hidden md:grid grid-cols-[1fr_1fr_140px_44px] gap-4 px-5 py-3.5 border-b border-border bg-muted/30">
              <p className="text-[13px] font-medium text-[#1e293b] dark:text-white">User</p>
              <p className="text-[13px] font-medium text-[#1e293b] dark:text-white">Responsibilities</p>
              <p className="text-[13px] font-medium text-[#1e293b] dark:text-white">Last Updated</p>
              <div />
            </div>

            <div className="divide-y divide-border">
              {pagSystemUsers.map((user) => (
                <div
                  key={user.id}
                  className="px-5 py-4 hover:bg-muted/40 transition-colors"
                >
                  {editingSystemUserId === user.id ? (
                    /* ── Inline edit form ── */
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <FormField label="Title" required>
                          <FormInput
                            value={editSystemUserForm.title}
                            onChange={(v) =>
                              setEditSystemUserForm((f) => ({
                                ...f,
                                title: v,
                              }))
                            }
                            placeholder="User title"
                          />
                        </FormField>
                        <FormField label="Short Name">
                          <FormInput
                            value={editSystemUserForm.shortName}
                            onChange={(v) =>
                              setEditSystemUserForm((f) => ({
                                ...f,
                                shortName: v,
                              }))
                            }
                            placeholder="Short name"
                          />
                        </FormField>
                      </div>
                      <FormField label="Description">
                        <FormTextarea
                          value={editSystemUserForm.description}
                          onChange={(v) =>
                            setEditSystemUserForm((f) => ({
                              ...f,
                              description: v,
                            }))
                          }
                          placeholder="Description"
                        />
                      </FormField>
                      <FormField label="Role IDs (comma-separated)">
                        <FormInput
                          value={editSystemUserForm.roleIds}
                          onChange={(v) =>
                            setEditSystemUserForm((f) => ({
                              ...f,
                              roleIds: v,
                            }))
                          }
                          placeholder="role-1, role-2"
                        />
                      </FormField>
                      <div className="grid grid-cols-2 gap-3">
                        <FormField label="Privilege Title">
                          <FormInput
                            value={editSystemUserForm.privilegeTitle}
                            onChange={(v) =>
                              setEditSystemUserForm((f) => ({
                                ...f,
                                privilegeTitle: v,
                              }))
                            }
                            placeholder="Privilege title"
                          />
                        </FormField>
                        <FormField label="Functions Performed (comma-separated)">
                          <FormInput
                            value={editSystemUserForm.functionsPerformed}
                            onChange={(v) =>
                              setEditSystemUserForm((f) => ({
                                ...f,
                                functionsPerformed: v,
                              }))
                            }
                            placeholder="func-1, func-2"
                          />
                        </FormField>
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <button
                          onClick={() => setEditingSystemUserId(null)}
                          className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleUpdateSystemUser}
                          disabled={
                            !editSystemUserForm.title || savingSystemUser
                          }
                          className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                        >
                          {savingSystemUser ? "Saving" : "Save"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* ── Read-only display ── */
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px_44px] gap-3 md:gap-4 items-start">
                      {/* User column */}
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="flex items-center justify-center size-10 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex-shrink-0 ring-1 ring-indigo-100 dark:ring-indigo-500/20">
                          <UserCog className="size-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-foreground">{user.title}</p>
                          {user.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                              {user.description}
                            </p>
                          )}
                          {(user.shortName || (user.roleIds && user.roleIds.length > 0)) && (
                            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                              {user.shortName && (
                                <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/20">
                                  {user.shortName}
                                </span>
                              )}
                              {user.roleIds?.map((rid) => (
                                <span
                                  key={rid}
                                  className="inline-flex items-center text-[10px] font-mono px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border"
                                >
                                  {rid}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Responsibilities column */}
                      <div className="min-w-0 md:pt-0.5">
                        {user.authorizedPrivileges?.length > 0 ? (
                          <div className="space-y-2">
                            {user.authorizedPrivileges.map((priv, i) => (
                              <div key={i} className="min-w-0">
                                <div className="flex items-start gap-2">
                                  <ShieldCheck className="size-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-foreground">{priv.title}</p>
                                    {(
                                      (priv as { functions_performed?: string[]; "functions-performed"?: string[] }).functions_performed ||
                                      (priv as { functions_performed?: string[]; "functions-performed"?: string[] })["functions-performed"]
                                    )?.length ? (
                                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                                        {(
                                          (priv as { functions_performed?: string[]; "functions-performed"?: string[] }).functions_performed ||
                                          (priv as { functions_performed?: string[]; "functions-performed"?: string[] })["functions-performed"]
                                        )?.join(", ")}
                                      </p>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>

                      {/* Last updated column */}
                      <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5 md:pt-1">
                        <Calendar className="size-3.5 flex-shrink-0" />
                        <span>{toddmmyyyy(user.updatedAt)}</span>
                      </div>

                      {/* Actions */}
                      {!isPublishedSsp ? (
                        <div className="flex items-center gap-1 flex-shrink-0 justify-end">
                          <Tip label="Edit System User" side="bottom">
                            <button
                              onClick={() => {
                                setEditingSystemUserId(user.id);
                                setEditSystemUserForm({
                                  title: user.title,
                                  shortName: user.shortName || "",
                                  description: user.description || "",
                                  roleIds: user.roleIds?.join(", ") || "",
                                  privilegeTitle: user.authorizedPrivileges?.[0]?.title || "",
                                  functionsPerformed:
                                    (user.authorizedPrivileges?.[0]?.["functions-performed"] as string[] | undefined)?.join(", ") || "",
                                });
                              }}
                              className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-muted-foreground hover:text-indigo-600 transition-colors"
                            >
                              <Pencil className="size-3.5" />
                            </button>
                          </Tip>
                          <Tip label="Delete System User" side="bottom">
                            <button
                              onClick={() => handleDeleteSystemUser(user.id)}
                              disabled={deletingSystemUserId === user.id}
                              className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          </Tip>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {filtSystemUsers.length > 0 && (
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015] flex-shrink-0">
              <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
                Showing{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {Math.min((tabPage - 1) * tabPageSize + 1, filtSystemUsers.length)}–{Math.min(tabPage * tabPageSize, filtSystemUsers.length)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {filtSystemUsers.length}
                </span>
              </p>
              {Math.ceil(filtSystemUsers.length / tabPageSize) > 1 && (
                <div className="flex items-center gap-0.5">
                  <button
                    disabled={tabPage === 1}
                    onClick={() => setCurrentTabPagination({ page: Math.max(1, tabPage - 1), pageSize: tabPageSize })}
                    className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="size-3" />
                  </button>

                  {getPageWindow(tabPage, Math.ceil(filtSystemUsers.length / tabPageSize)).map((p, idx) =>
                    p === "dots" ? (
                      <span
                        key={`dots-${idx}`}
                        className="w-6 h-6 flex items-center justify-center text-xs text-[#475569] dark:text-white/70 select-none"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentTabPagination({ page: p as number, pageSize: tabPageSize })}
                        className={`w-6 h-6 text-xs font-medium rounded-md transition-colors ${p === tabPage
                          ? "bg-indigo-600 dark:bg-indigo-600 text-white"
                          : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                          }`}
                      >
                        {p}
                      </button>
                    ),
                  )}

                  <button
                    disabled={tabPage >= Math.ceil(filtSystemUsers.length / tabPageSize)}
                    onClick={() => setCurrentTabPagination({ page: Math.min(Math.ceil(filtSystemUsers.length / tabPageSize), tabPage + 1), pageSize: tabPageSize })}
                    className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="size-3" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
          <UserCog className="size-8 text-muted-foreground mb-3 mx-auto" />
          <p className="text-sm font-medium">No system users defined</p>
          <p className="text-xs text-muted-foreground mt-1">
            Add user types and their authorized privileges
          </p>
        </div>
      )}
    </>
  );
}
